import { Dispatch, Fragment, SetStateAction, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import { z } from "zod"
import axios from "axios"
import { parseCookies } from "nookies"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronDown, ChevronsUpDown, ChevronUp, Download } from "lucide-react"

// ** Theme Components
import { Input } from "@components/common/input"
import { Label } from "@components/common/label"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { Checkbox } from "@components/common/checkbox"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pma/components/common/table"

// ** Routes
import {
  ALL_ESTIMATE_BOQ_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_CONTRACTOR_COMPANY_LIST,
  GET_ESTIMATE_BOQ_ENDPOINT,
  MAIN_BOQ_BULK_ENDPOINT,
  MAIN_BOQ_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Types
import { DictType } from "@pma/types"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Helpers
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"

// ** Page Routes
import {
  FINANCIAL_BOQ_MAIN_ROUTE,
  FINANCIAL_BOQ_ROUTE,
  PROJECTS_PAGE_ROUTE,
} from "@pma/utils/page-routes"

// ** Stores
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Utils
import { getFilteredProjectsByCurrentUser } from "@pma/utils/utils"

// ** Custom Components
import SearchableSelect from "../common/SearchSelect"
import { PriceListExcelMapper } from "./excel-mapper"
import CreateNewBoQEstimate from "./create-new-boq-estimate"
import InsertUpdateContractorCompanyDialog from "../Contractor-Company/InsertUpdateContractorCompanyDialog"

const boQSchema = z.object({
  colunm_name_number: z.string().optional(),
  colum_name_unit: z.string({ required_error: "" }).optional(),
  colum_name_price: z.string().optional(),
  boq_name: z.string().optional(),
  column_name_amount: z.string().optional(),
  column_name_description: z.string().optional(),
  project_id: z.number(),
  discount: z
    .string()
    .transform((val) => {
      const parsed = parseFloat(val)
      return isNaN(parsed) ? undefined : parsed
    })
    .optional(),
  estimate_boq_id: z.number(),
  company_id: z.number(),
})

interface Props {
  revalidate: CallableFunction
  companyId?: string
  projectId?: string
  estimateBoQId?: string
  asLink?: boolean
  estimateItems?: string[]
  downloadTemp?: boolean
  createMainBoQ?: boolean
  setCreateMainBoQ?: Dispatch<SetStateAction<boolean>>
}
const CreateMainBoQ = ({
  revalidate,
  companyId,
  projectId,
  estimateBoQId,
  asLink,
  estimateItems,
  downloadTemp,
  createMainBoQ = false,
  setCreateMainBoQ,
}: Props) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const { authConfig, setTabManager } = useAuth()

  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)

  const [boqName, setBoqName] = useState("")
  const [uploadExcel, setUploadExcel] = useState(false)
  const [isItemsOpen, setIsItemsOpen] = useState(true)
  const [createEstimate, setCreateEstimate] = useState(false)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showUploadDialog, setShowUploadDialog] = useState(createMainBoQ)
  const [contractorCompanyDialog, setContractorCompanyDialog] = useState(false)
  const [newContractorCompanyDetails, setNewContractorCompanyDetails] = useState<any>(null)
  const [fileInformation, setFileInformation] = useState<DictType>({
    Name: "",
    Number: "",
    Unit: "",
    Price: "",
    Description: "",
    File: "",
  })

  const form = useForm<z.infer<typeof boQSchema>>({
    resolver: zodResolver(boQSchema),
    defaultValues: {
      boq_name: "",
      colunm_name_number: "",
      colum_name_unit: "",
      colum_name_price: "",
      column_name_description: "",
      company_id: companyId ? parseInt(companyId) : 0,
      project_id: projectId ? parseInt(projectId) : 0,
      estimate_boq_id: estimateBoQId ? parseInt(estimateBoQId) : 0,
    },
  })

  const getContractorCompanyListApi = async () => {
    const params: any = { organization_id: orgId }

    const response = await axios.get(FINANCIAL_SERVER_URL + GET_CONTRACTOR_COMPANY_LIST, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: companies = [], refetch: refetchCompanies } = useQuery({
    queryKey: ["contractor-companies", orgId],
    queryFn: getContractorCompanyListApi,
  })

  const { data: projects } = useQuery({
    queryKey: ["projects", orgId],
    queryFn: () =>
      getFilteredProjectsByCurrentUser({
        by_organization_id: orgId,
        status_id: 0,
        paging_per_page: 30,
      }),
    enabled: showUploadDialog,
  })

  const getEstimateBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_ESTIMATE_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        project_id: projectId,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: estimateBoQs, refetch: refetchEstimateBoQs } = useQuery({
    queryKey: ["estimate-boq", orgId],
    queryFn: getEstimateBoQs,
    enabled: showUploadDialog,
  })

  const formEstimateBoQId = form.watch("estimate_boq_id")

  const getEstimateBoQItems = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_ESTIMATE_BOQ_ENDPOINT,
      { boq_estimate_id: formEstimateBoQId },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const canBoqItems = !estimateItems && !!formEstimateBoQId

  const {
    data: estimateBoQItemsData,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: ["estimate-boq-items", formEstimateBoQId],
    queryFn: getEstimateBoQItems,
    enabled: canBoqItems,
  })

  const estimateBoQItems = estimateBoQItemsData?.boq_items

  const addBoQEstimate = async (data: z.infer<typeof boQSchema>) => {
    const excelFile = fileInformation?.File
    const response = await axios.post(
      FINANCIAL_SERVER_URL + MAIN_BOQ_ENDPOINT,
      {
        colunm_name_number: fileInformation.Number || undefined,
        colum_name_unit: fileInformation?.Unit || undefined,
        colum_name_price: fileInformation?.Price || undefined,
        column_name_description: fileInformation?.Description || undefined,
        boq_name: fileInformation?.Name || excelFile[0]?.name,
        column_name_amount: fileInformation?.Amount,
        project_id: data?.project_id,
        xls_file: excelFile[0],
        company_id: data?.company_id,
        discount: data?.discount || undefined,
        estimate_boq_id: data?.estimate_boq_id || estimateBoQId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  }

  const addBoQEstimateFromItems = async (data: z.infer<typeof boQSchema>) => {
    const items = estimateItems || selectedItems
    const response = await axios.post(
      FINANCIAL_SERVER_URL + MAIN_BOQ_BULK_ENDPOINT,
      {
        boq_name: boqName,
        project_id: data?.project_id,
        company_id: data?.company_id,
        discount: data?.discount || undefined,
        estimate_boq_id: data?.estimate_boq_id || estimateBoQId,
        estimate_item_id: items,
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )

    return response.data
  }

  const addBoQMutation = useMutation({
    mutationKey: ["boq-estimate"],
    mutationFn: uploadExcel ? addBoQEstimate : addBoQEstimateFromItems,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("BOQ added successfully"),
        variant: "success",
      })

      revalidate && revalidate()
      queryClient.invalidateQueries({ queryKey: ["main-boq"] })

      closeDialog()
      setFileInformation({})

      if (projectId) {
        newParams.delete("estimate")
        newParams.set("main", data?.id)

        setTabManager({
          group: "boq",
          main: "main",
          sub: "boq-tender",
          subChild: "boq-items",
        })

        return navigate(PROJECTS_PAGE_ROUTE + `/${projectId}?${newParams.toString()}`)
      }

      navigate(FINANCIAL_BOQ_MAIN_ROUTE + `/${data?.id}`)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "create boq", translate),
        variant: "error",
      })
    },
  })

  const selectedProject = useMemo(
    () =>
      projects?.items?.find(
        (project: DictType) => project.id == String(form.getValues("project_id")),
      ),
    [form.watch("project_id"), form],
  )

  const handleItemToggle = (itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    )
  }

  const selectedEstimateBoQ = () =>
    estimateBoQs?.find(
      (estimate: DictType) =>
        estimate.id == String(form.getValues("estimate_boq_id")) ||
        estimate.id == String(estimateBoQId),
    )

  function onSubmit(data: z.infer<typeof boQSchema>) {
    addBoQMutation.mutate({ ...data })
  }

  const toggleDefaultOpen = (isOpen: boolean) => {
    refetchEstimateBoQs()
    setCreateEstimate(isOpen)
  }

  const downloadTemplate = (language: string) => {
    const templateUrl = `${process.env.PUBLIC_URL}/assets/boq-template-${language}.xlsx`
    const link = document.createElement("a")
    link.href = templateUrl
    link.setAttribute("download", `boq-template-${language}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    if (isFetched) {
      setSelectedItems(estimateBoQItems?.map((item: DictType) => item?.id))
    }
  }, [isFetched, formEstimateBoQId])

  useEffect(() => {
    if (!isLoading && estimateBoQs?.length === 0) {
      setCreateEstimate(true)
    }

    return () => {
      setCreateEstimate(false)
    }
  }, [isLoading])

  useEffect(() => {
    if (showUploadDialog != false) {
      setBoqName("")
      form.reset()
    }
  }, [showUploadDialog])

  const contractorList = useMemo(() => {
    const options: any[] = []
    if (companies) companies.map((user: any) => options.push({ ...user, label: user.name }))
    return options
  }, [companies])

  const closeDialog = () => {
    setShowUploadDialog(false)
    if (setCreateMainBoQ) setCreateMainBoQ(false)
  }

  useEffect(() => {
    if (newContractorCompanyDetails) {
      form.setValue("company_id", newContractorCompanyDetails.id, { shouldValidate: false })
      setNewContractorCompanyDetails(null)
    }
  }, [newContractorCompanyDetails])

  return (
    <Fragment>
      <Dialog open={showUploadDialog} onOpenChange={closeDialog}>
        <div className='flex gap-2'>
          {estimateBoQId ? (
            <Button
              size={"sm"}
              variant={asLink ? "link" : "default"}
              className={cn("h-7 py-0 text-xs")}
              disabled={estimateItems && estimateItems?.length === 0}
              onClick={() => setShowUploadDialog(true)}
            >
              {translate("Generate Main BoQ")}
              {estimateItems && `(${estimateItems?.length})`}
            </Button>
          ) : (
            <Button
              onClick={() => setShowUploadDialog(true)}
              size={"sm"}
              variant={asLink ? "link" : "default"}
              className='text-xs'
            >
              {translate("Create Main BoQ")}
            </Button>
          )}

          {downloadTemp && (
            <Button
              onClick={() => downloadTemplate(locale)}
              size={"sm"}
              className='gap-2 text-xs'
              variant={"secondary"}
            >
              <Download className='h-4 w-4' />
              {translate("Download Template")}
            </Button>
          )}
        </div>

        <DialogContent
          className={cn(
            "flex h-5/6 w-full flex-col gap-2 overflow-scroll p-0 md:max-w-2xl lg:max-w-4xl xl:max-w-7xl",
            isHebrew ? "rtl" : "ltr",
          )}
        >
          <DialogHeader className='flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4'>
            <DialogTitle
              className={cn(
                "text-base sm:text-lg lg:text-xl",
                isHebrew ? "text-right" : "text-left",
              )}
            >
              {translate("Add new Bill of Quantities")}
            </DialogTitle>
            {estimateItems && (
              <DialogDescription className='text-xs sm:text-sm'>
                {`(${estimateItems?.length}) ${translate("items included")}`}.
              </DialogDescription>
            )}
          </DialogHeader>
          <div className='overflow-y-auto px-4 sm:px-6'>
            {uploadExcel ? (
              <Button
                variant={"link"}
                size={"sm"}
                className='flex w-full justify-end p-0 text-xs sm:text-sm'
                onClick={() => setUploadExcel(!uploadExcel)}
                type='button'
              >
                {translate("Select Items from estimate")}?
              </Button>
            ) : null}

            <div className='grid gap-3 sm:gap-4'>
              {(estimateItems || !uploadExcel) && (
                <div className='flex flex-col'>
                  <div className='mb-1.5 flex w-full justify-between'>
                    <Label className='text-xs capitalize sm:text-sm'>{translate("Name")}</Label>
                  </div>
                  <Input
                    placeholder={translate("Enter BoQ name")}
                    value={boqName}
                    onChange={(e) => setBoqName(e.target.value)}
                    required
                    className='text-xs text-black sm:text-sm'
                  />
                </div>
              )}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className=''>
                  <div className='grid grid-cols-1 gap-2 pb-3 sm:grid-cols-2 sm:gap-3'>
                    <FormField
                      control={form.control}
                      name={`company_id`}
                      render={({ field }) => (
                        <FormItem
                          className={cn(
                            "flex flex-col items-start justify-start space-y-2",
                            companyId && "hidden",
                          )}
                        >
                          <div className='flex w-full items-center justify-between'>
                            <FormLabel className='text-xs sm:text-sm'>
                              {translate("Contractor Company")}
                              <span className='text-destructive'>*</span>
                            </FormLabel>
                          </div>
                          <SearchableSelect
                            placeholder='Select Contractor'
                            options={contractorList}
                            onSelect={(option) => field.onChange(option?.id)}
                            onAddOption={(value) => setContractorCompanyDialog(value)}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`project_id`}
                      render={({ field }) => (
                        <FormItem
                          className={cn(
                            "flex flex-col items-start justify-start space-y-2",
                            projectId && "hidden",
                          )}
                        >
                          <FormLabel className='text-xs sm:text-sm'>
                            {translate("Project")}
                            <span className='text-destructive'>*</span>
                          </FormLabel>
                          <Popover>
                            <FormControl>
                              <PopoverTrigger
                                disabled={addBoQMutation.isPending || !!projectId}
                                className='flex w-full items-center justify-between rounded-md border p-1.5 text-xs sm:text-sm'
                              >
                                <div className='truncate text-xs sm:text-sm'>
                                  {field?.value ? (
                                    <div className='overflow-hidden truncate text-ellipsis p-0.5 text-left text-xs font-semibold capitalize sm:text-sm'>
                                      {selectedProject?.title}
                                    </div>
                                  ) : (
                                    translate("Select Project")
                                  )}
                                </div>
                                <ChevronsUpDown size={12} className='ml-2 flex-shrink-0' />
                              </PopoverTrigger>
                            </FormControl>
                            <PopoverContent className='w-full p-0 sm:w-56'>
                              <Command>
                                <CommandInput
                                  placeholder={translate("Search for project") + "..."}
                                  className='text-xs sm:text-sm'
                                />
                                <CommandList>
                                  <CommandEmpty className='text-xs sm:text-sm'>
                                    {translate("No projects found")}.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {projects?.items?.map((project: DictType) => (
                                      <PopoverClose className='w-full' key={project?.id}>
                                        <CommandItem
                                          key={project.id}
                                          value={project?.title}
                                          defaultValue={project?.title}
                                          onSelect={() => {
                                            field.onChange(project?.id)
                                          }}
                                          className={cn(
                                            "my-1 text-xs capitalize sm:text-sm",
                                            field?.value === project.id &&
                                              "bg-secondary/30 text-black",
                                          )}
                                        >
                                          {project?.title}
                                        </CommandItem>
                                      </PopoverClose>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='discount'
                      render={({ field }) => (
                        <FormItem className='flex flex-col items-start justify-start space-y-2'>
                          <FormLabel className='text-xs capitalize sm:text-sm'>
                            {translate("Discount")}(%)
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("Discount")}
                              className='text-xs text-black sm:text-sm'
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  {!estimateBoQId && !uploadExcel && (
                    <div className='grid gap-2 pb-2'>
                      <FormField
                        control={form.control}
                        name={`estimate_boq_id`}
                        render={({ field }) => (
                          <FormItem
                            className={cn(
                              "flex flex-col items-start justify-start space-y-2",
                              estimateBoQId && "hidden",
                            )}
                          >
                            <div className='flex w-full items-center justify-between'>
                              <FormLabel className='text-xs sm:text-sm'>
                                {translate("BoQ Estimate")}
                                <span className='text-destructive'>*</span>
                              </FormLabel>
                              <Button
                                variant={"link"}
                                size={"sm"}
                                className='m-0 p-0 text-xs text-primary sm:text-sm'
                                onClick={() => setCreateEstimate(true)}
                                type='button'
                              >
                                {translate("Create new estimate")}?
                              </Button>
                            </div>
                            <Popover>
                              <FormControl>
                                <PopoverTrigger
                                  disabled={addBoQMutation.isPending || !!estimateBoQId}
                                  className='flex w-full items-center justify-between rounded-md border p-1.5 text-xs sm:text-sm'
                                >
                                  <div className='truncate text-xs sm:text-sm'>
                                    {field?.value ? (
                                      <div className='overflow-hidden truncate text-ellipsis p-0.5 text-left text-xs font-semibold capitalize sm:text-sm'>
                                        {selectedEstimateBoQ()?.name}
                                      </div>
                                    ) : (
                                      translate("Select BoQ")
                                    )}
                                  </div>
                                  <ChevronsUpDown size={12} className='ml-2 flex-shrink-0' />
                                </PopoverTrigger>
                              </FormControl>
                              <PopoverContent className='w-full p-0 sm:w-56'>
                                <Command>
                                  <CommandInput
                                    placeholder={translate("Search for estimate") + "..."}
                                    className='text-xs sm:text-sm'
                                  />
                                  <CommandList>
                                    <CommandEmpty className='text-xs sm:text-sm'>
                                      {translate("No estimates found")}.
                                    </CommandEmpty>
                                    <CommandEmpty>
                                      <Button
                                        variant={"link"}
                                        onClick={() => {
                                          setTabManager({
                                            group: "boq",
                                            main: "estimates",
                                            sub: undefined,
                                          })
                                          navigate(FINANCIAL_BOQ_ROUTE)
                                        }}
                                        className='text-xs sm:text-sm'
                                      >
                                        {translate("Go to estimates")}.
                                      </Button>
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {estimateBoQs?.map((estimate: DictType) => (
                                        <PopoverClose className='w-full' key={estimate.id}>
                                          <CommandItem
                                            value={estimate?.title}
                                            defaultValue={estimate?.title}
                                            onSelect={() => {
                                              field.onChange(estimate?.id)
                                            }}
                                            className={cn(
                                              "my-1 text-xs capitalize sm:text-sm",
                                              field?.value === estimate.id &&
                                                "bg-secondary/30 text-black",
                                            )}
                                          >
                                            {estimate?.name}
                                          </CommandItem>
                                        </PopoverClose>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>

                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {!estimateItems && formEstimateBoQId && !uploadExcel ? (
                    <div className='flex w-full flex-col'>
                      <div className='flex flex-col justify-between gap-2 sm:flex-row'>
                        <div
                          className='flex cursor-pointer items-center justify-between'
                          onClick={() => setIsItemsOpen(!isItemsOpen)}
                        >
                          <Label className='text-xs font-medium sm:text-sm'>
                            {translate("Estimate Items")} ({selectedItems?.length})
                          </Label>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='ml-2 h-5 w-5 flex-shrink-0 p-0'
                            aria-expanded={isItemsOpen}
                            aria-controls='estimate-items'
                            type='button'
                          >
                            {isItemsOpen ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            )}
                          </Button>
                        </div>
                        <Button
                          variant={"link"}
                          size={"sm"}
                          className='m-0 justify-start p-0 text-xs sm:justify-end sm:text-sm'
                          onClick={() => setUploadExcel(true)}
                          type='button'
                        >
                          {translate("Upload new estimate file")}?
                        </Button>
                      </div>

                      <div
                        id='estimate-items'
                        className={cn(
                          "my-2 h-auto max-h-[40vh] w-full overflow-y-auto rounded-md",
                          isItemsOpen ? "flex" : "hidden",
                        )}
                      >
                        <div className='w-full overflow-x-auto'>
                          <Table className='rounded-t-md text-xs sm:text-sm'>
                            <TableHeader className='h-8 rounded-t-md bg-primary/20 p-0 sm:h-10'>
                              <TableRow className='h-8 p-0 font-semibold sm:h-10'>
                                <TableHead className='h-full w-10 p-1 sm:p-0.5'>
                                  <div className='mx-2 flex items-center'>
                                    <Checkbox
                                      id={`select-all`}
                                      checked={selectedItems?.length === estimateBoQItems?.length}
                                      onCheckedChange={() => {
                                        if (selectedItems?.length === estimateBoQItems?.length) {
                                          setSelectedItems([])
                                        } else {
                                          setSelectedItems(
                                            estimateBoQItems?.map((item: DictType) => item?.id),
                                          )
                                        }
                                      }}
                                    />
                                  </div>
                                </TableHead>
                                <TableHead
                                  className={cn(
                                    "h-full min-w-16 p-1 sm:p-0.5",
                                    isHebrew ? "text-right" : "text-left",
                                  )}
                                >
                                  {translate("Number")}
                                </TableHead>
                                <TableHead
                                  className={cn(
                                    "h-full min-w-32 p-1 sm:p-0.5",
                                    isHebrew ? "text-right" : "text-left",
                                  )}
                                >
                                  {translate("Description")}
                                </TableHead>
                                <TableHead
                                  className={cn(
                                    "h-full min-w-16 p-1 sm:p-0.5",
                                    isHebrew ? "text-right" : "text-left",
                                  )}
                                >
                                  {translate("Units")}
                                </TableHead>
                                <TableHead
                                  className={cn(
                                    "h-full min-w-16 p-1 sm:p-0.5",
                                    isHebrew ? "text-right" : "text-left",
                                  )}
                                >
                                  {translate("Amount")}
                                </TableHead>
                                <TableHead
                                  className={cn(
                                    "h-full min-w-16 p-1 sm:p-0.5",
                                    isHebrew ? "text-right" : "text-left",
                                  )}
                                >
                                  {translate("Price")}
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody className={`space-y-2 overflow-y-scroll pt-2`}>
                              {estimateBoQItems?.map((boqItem: DictType) => (
                                <TableRow key={boqItem?.id} className='text-xs sm:text-sm'>
                                  <TableCell className='p-1 sm:p-0.5'>
                                    <div
                                      key={boqItem?.id}
                                      className={cn(
                                        "mx-2 flex items-start",
                                        isHebrew ? "rtl" : "ltr",
                                      )}
                                    >
                                      <Checkbox
                                        id={`item-${boqItem.id}`}
                                        checked={selectedItems?.includes(boqItem?.id)}
                                        onCheckedChange={() => handleItemToggle(boqItem.id)}
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell className='p-1 font-medium sm:p-0.5'>
                                    {boqItem?.number}
                                  </TableCell>

                                  <TableCell className='text-wrap p-1 sm:p-0.5'>
                                    {boqItem?.description || "---"}
                                  </TableCell>
                                  <TableCell className='p-1 sm:p-0.5'>
                                    {boqItem?.units || "---"}
                                  </TableCell>
                                  <TableCell className='p-1 sm:p-0.5'>{boqItem?.amount}</TableCell>

                                  <TableCell className='p-1 sm:p-0.5'>{boqItem?.price}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {isLoading && (
                          <div className='my-2 flex h-[50px] w-full items-center justify-center rounded-md shadow-lg'>
                            <PmsLoader />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}

                  {uploadExcel && (
                    <div className='flex w-full flex-col items-center justify-center overflow-auto'>
                      <PriceListExcelMapper setMapping={setFileInformation} mappingType='boq' />
                    </div>
                  )}

                  <DialogFooter className='flex-shrink-0 flex-wrap gap-2 border-t px-4 pt-2 sm:flex-nowrap sm:px-6 sm:pt-3'>
                    <Button
                      type='button'
                      variant='outline'
                      size={"sm"}
                      className='text-xs sm:text-sm'
                      onClick={closeDialog}
                    >
                      {translate("Cancel")}
                    </Button>

                    {!estimateItems && uploadExcel && (
                      <Button
                        type='submit'
                        size={"sm"}
                        className='text-xs sm:text-sm'
                        disabled={!fileInformation?.File || !form.watch("company_id")}
                        loading={addBoQMutation.isPending}
                      >
                        {addBoQMutation.isPending ? translate("Uploading") : translate("Upload")}
                      </Button>
                    )}
                    {(estimateItems || !uploadExcel) && (
                      <Button
                        type='submit'
                        size={"sm"}
                        className='text-xs sm:text-sm'
                        disabled={
                          !boqName ||
                          addBoQMutation.isPending ||
                          !form.watch("project_id") ||
                          !form.watch("company_id")
                        }
                        loading={addBoQMutation.isPending}
                      >
                        {addBoQMutation.isPending ? translate("Creating") : translate("Create")}
                      </Button>
                    )}
                  </DialogFooter>
                </form>
              </Form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {contractorCompanyDialog && (
        <InsertUpdateContractorCompanyDialog
          refetchList={refetchCompanies}
          insertUpdateContractorCompanyDialog={contractorCompanyDialog}
          setNewContractorCompanyDetails={setNewContractorCompanyDetails}
          setInsertUpdateContractorCompanyDialog={setContractorCompanyDialog}
        />
      )}

      {createEstimate && (
        <CreateNewBoQEstimate
          projectId={projectId}
          defaultOpen={createEstimate}
          setDefaultOpen={toggleDefaultOpen}
        />
      )}
    </Fragment>
  )
}

export default CreateMainBoQ
